import { forwardRef, useContext, useEffect } from 'react';
import { combineClassNames } from '../../Utils/ClassnameUtils';
import GWPModalContext from './GWPModalContext';

export type GWPModalFramePadding = 'default' | 'dense' | 'none';
export type GWPModalFrameSurfaceStyle = 'flat' | 'layered';

export interface GWPModalFrameProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> {
	title?: React.ReactNode;
	description?: React.ReactNode;
	footer?: React.ReactNode;
	leftActions?: React.ReactNode;
	rightActions?: React.ReactNode;
	padding?: GWPModalFramePadding;
	surfaceStyle?: GWPModalFrameSurfaceStyle;
	bodyClassName?: string;
	footerClassName?: string;
}

const GWPModalFrame = forwardRef<HTMLDivElement, GWPModalFrameProps>((props, ref) => {
	const {
		title,
		description,
		footer,
		leftActions,
		rightActions,
		padding = 'default',
		surfaceStyle = 'flat',
		className,
		bodyClassName,
		footerClassName,
		children,
		...rest
	} = props;

	const modalContext = useContext(GWPModalContext);
	const hasHeader = title !== undefined || description !== undefined;
	const hasMain = hasHeader || children !== undefined;
	const hasFooter = footer !== undefined || leftActions !== undefined || rightActions !== undefined;

	useEffect(() => {
		modalContext?.registerTitle(title !== undefined);
		modalContext?.registerDescription(description !== undefined);

		return () => {
			modalContext?.registerTitle(false);
			modalContext?.registerDescription(false);
		};
	}, [description, modalContext, title]);

	return (
		<div ref={ref} className={combineClassNames('gwp-modal-frame', surfaceStyle, className)} {...rest}>
			{hasMain && (
				<div className="gwp-modal-frame-main">
					{hasHeader && (
						<div className="gwp-modal-frame-header">
							{title !== undefined && (
								<h2 id={modalContext?.titleId} className="gwp-modal-frame-title">
									{title}
								</h2>
							)}
							{description !== undefined && (
								<p id={modalContext?.descriptionId} className="gwp-modal-frame-description">
									{description}
								</p>
							)}
						</div>
					)}

					{children !== undefined && (
						<div className={combineClassNames('gwp-modal-frame-body', padding, bodyClassName)}>{children}</div>
					)}
				</div>
			)}

			{hasFooter && (
				<div className={combineClassNames('gwp-modal-frame-footer', footerClassName)}>
					{footer !== undefined ? (
						footer
					) : (
						<>
							<div className="gwp-modal-frame-footer-left">{leftActions}</div>
							<div className="gwp-modal-frame-footer-right">{rightActions}</div>
						</>
					)}
				</div>
			)}
		</div>
	);
});

GWPModalFrame.displayName = 'GWPModalFrame';

export default GWPModalFrame;
